import PropTypes from "prop-types";
import MetaTags from "react-meta-tags";
import React, { useState } from "react";
import { Alert } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { withRouter, Link } from "react-router-dom";
import { userForgetPassword } from "../../store/actions";

// Icons from react-icons
import {
  RiToothLine,
  RiShieldCheckLine,
  RiMailLine,
  RiArrowRightLine,
  RiLockPasswordLine,
  RiTimeLine,
  RiCustomerService2Line,
} from "react-icons/ri";

import oralstopLogo from "../../assets/images/logos/oralstop-logo.png";

import "./auth-custom.scss";

const ForgetPasswordPage = (props) => {
  const dispatch = useDispatch();

  const [username, setUsername] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  const { forgetError, forgetSuccessMsg } = useSelector((state) => ({
    forgetError: state.ForgetPassword?.forgetError,
    forgetSuccessMsg: state.ForgetPassword?.forgetSuccessMsg,
  }));

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!username.trim()) {
      setValidationError("Please enter your registered email address or mobile number.");
      return;
    }

    setIsSubmitting(true);
    setValidationError("");
    dispatch(userForgetPassword({ email: username.trim(), username: username.trim() }, props.history));

    setTimeout(() => {
      setIsSubmitting(false);
    }, 1200);
  };

  const features = [
    {
      icon: <RiLockPasswordLine />,
      title: "Secure Account Recovery",
      desc: "One-click reset instructions sent to your registered address",
    },
    {
      icon: <RiShieldCheckLine />,
      title: "Encrypted Security Tokens",
      desc: "Protected cryptographic authentication tokens",
    },
    {
      icon: <RiTimeLine />,
      title: "24-Hour Link Expiry",
      desc: "Time-bound recovery links for maximum practice security",
    },
    {
      icon: <RiCustomerService2Line />,
      title: "OPD Technical Support",
      desc: "Need immediate assistance? Contact clinic administrator",
    },
  ];

  return (
    <React.Fragment>
      <MetaTags>
        <title>Forgot Password | Oralstop OPD - Clinical Management System</title>
      </MetaTags>

      <div className="dental-auth-wrapper">
        <div className="dental-auth-card">
          {/* Left Hero Panel */}
          <div className="dental-hero-panel">
            <div>
              {/* Brand Logo & Header */}
              <div className="dental-brand-header">
                <img src={oralstopLogo} alt="oralstop.com" height="38" className="dental-brand-logo-img" />
                <div>
                  <span className="dental-brand-sub">Clinical EHR Platform</span>
                </div>
              </div>

              {/* Main Heading */}
              <h2 className="dental-hero-heading">
                Account Recovery & <span>Password Security</span>.
              </h2>
              <p className="dental-hero-desc">
                Easily regain access to your clinic records, doctor schedules, and patient management portal with verified password resets.
              </p>

              {/* Clinical Feature List */}
              <div className="dental-features-list">
                {features.map((feat, idx) => (
                  <div key={idx} className="dental-feature-item">
                    <div className="dental-feature-icon">{feat.icon}</div>
                    <div className="dental-feature-text">
                      <h6>{feat.title}</h6>
                      <p>{feat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Security Badge */}
            <div className="dental-security-badge">
              <span className="pulse-dot" />
              <RiShieldCheckLine className="me-1" />
              <span>256-bit AES Encrypted & HIPAA Compliant</span>
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="dental-form-panel">
            <div className="dental-form-container">
              <div className="dental-form-header">
                <h3 className="dental-form-title">Forgot Password?</h3>
                <p className="dental-form-subtitle">
                  Enter your registered email address or mobile to receive recovery instructions.
                </p>
              </div>

              {/* Alerts */}
              {forgetSuccessMsg && (
                <Alert color="success" className="mb-3 d-flex align-items-center py-2 px-3">
                  <i className="mdi mdi-check-circle-outline me-2 font-size-16" />
                  <div className="font-size-13">{forgetSuccessMsg}</div>
                </Alert>
              )}

              {(forgetError || validationError) && (
                <Alert color="danger" className="mb-3 d-flex align-items-center py-2 px-3">
                  <i className="mdi mdi-alert-circle-outline me-2 font-size-16" />
                  <div className="font-size-13">{forgetError || validationError}</div>
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                {/* Email / Mobile Field */}
                <div className="custom-auth-group">
                  <label className="custom-auth-label" htmlFor="username-input">
                    Registered Email or Mobile Number
                  </label>
                  <div className="custom-input-box">
                    <RiMailLine className="input-icon-prefix" />
                    <input
                      id="username-input"
                      name="username"
                      type="text"
                      className="form-control dental-input-field"
                      placeholder="doctor@oralstop.com"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        if (validationError) setValidationError("");
                      }}
                      required
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="dental-submit-btn mt-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span>Sending recovery instructions...</span>
                  ) : (
                    <>
                      <span>Send Reset Link</span>
                      <RiArrowRightLine className="font-size-16" />
                    </>
                  )}
                </button>
              </form>

              {/* Direct Link to Reset Password Page */}
              <div className="mt-3 text-center">
                <Link to="/reset-password" className="forgot-link font-size-12">
                  Already have a reset token? Set New Password →
                </Link>
              </div>

              {/* Footer */}
              <div className="auth-footer-text">
                <span>Remember your credentials?</span>
                <Link to="/login" className="signup-link">
                  Sign In
                </Link>
                <div className="mt-2 text-muted font-size-11">
                  © {new Date().getFullYear()} Oralstop OPD · All Rights Reserved
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

ForgetPasswordPage.propTypes = {
  history: PropTypes.object,
};

export default withRouter(ForgetPasswordPage);
